import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { GoogleAnalyticsService } from '@/services/googleAnalytics';

interface PageTrackerProps {
  children: React.ReactNode;
}

export const PageTracker: React.FC<PageTrackerProps> = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    // Track page view
    const trackPageView = async () => {
      try {
        // Track in our internal analytics
        await fetch('/api/analytics/track/page-view', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            page: location.pathname,
            timestamp: new Date(),
            userAgent: navigator.userAgent,
            referrer: document.referrer,
            title: document.title
          }),
        });

        // Track in Google Analytics
        GoogleAnalyticsService.trackPageView(location.pathname, document.title);

        // Track capability page views with enhanced data
        if (location.pathname.startsWith('/capabilities/')) {
          const capabilitySlug = location.pathname.replace('/capabilities/', '');
          const capabilityName = capabilitySlug.split('-').map(word =>
            word.charAt(0).toUpperCase() + word.slice(1)
          ).join(' ');
          GoogleAnalyticsService.trackCapabilityView(capabilityName, capabilitySlug);
        }

        // Track service page views
        if (location.pathname === '/services') {
          GoogleAnalyticsService.trackServiceInterest('Services Overview', 'navigation');
        }

      } catch (error) {
        console.error('Page view tracking failed:', error);
      }
    };

    // Track page view on route change
    trackPageView();

    // Track time on page
    const startTime = Date.now();
    
    return () => {
      const timeOnPage = Date.now() - startTime;
      
      // Track time spent on page
      fetch('/api/analytics/track/time-on-page', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          page: location.pathname,
          timeOnPage,
          timestamp: new Date()
        }),
      }).catch(error => {
        console.error('Time on page tracking failed:', error);
      });
    };
  }, [location.pathname]);

  return <>{children}</>;
};